import React, { useState, useEffect } from 'react';
import {
  BookOpen, Book, Heart, LogIn, Search, X, SlidersHorizontal,
  ChefHat, Plus, Star, Clock, Utensils, TrendingUp,
  Grid, List, Users, Sun, Moon, Coffee, Apple, Cake, Sandwich, Globe,
} from 'lucide-react';
import {
  useRecipes,
  useFeaturedRecipes,
  useUserRecipes,
  useRecipeFavorites,
  useRecipe,
  getRecipeImageURL,
} from '../../hooks/useRecipes';
import { useDietaryFilter } from '../../hooks/useDietaryFilter';
import { auth } from '../../services/firebase';
import { useSearchParams, useNavigate, useParams } from 'react-router-dom';
import RecipeCard from './RecipeCard';
import RecipeDetailView from './RecipeDetailView';
import RecipeSubmissionView from './RecipeSubmissionView';
import CookbookHub from './CookbookHub';
import CookbookBrowse from './CookbookBrowse';
import CookbookDetail from './CookbookDetail';
import { Card, Button } from '../UI';

const MEAL_TYPE_ICONS = { breakfast: Sun, lunch: Sandwich, dinner: Moon, snack: Apple, dessert: Cake, beverage: Coffee };

const MEAL_TYPES = [
  { value: 'breakfast', label: 'Breakfast' },
  { value: 'lunch',     label: 'Lunch'     },
  { value: 'dinner',    label: 'Dinner'    },
  { value: 'snack',     label: 'Snack'     },
  { value: 'dessert',   label: 'Dessert'   },
  { value: 'beverage',  label: 'Beverage'  },
];

const CUISINE_TYPES = [
  { value: 'american',      label: 'American'       },
  { value: 'italian',       label: 'Italian'        },
  { value: 'mexican',       label: 'Mexican'        },
  { value: 'chinese',       label: 'Chinese'        },
  { value: 'japanese',      label: 'Japanese'       },
  { value: 'thai',          label: 'Thai'           },
  { value: 'indian',        label: 'Indian'         },
  { value: 'french',        label: 'French'         },
  { value: 'mediterranean', label: 'Mediterranean'  },
  { value: 'middleEastern', label: 'Middle Eastern' },
  { value: 'greek',         label: 'Greek'          },
  { value: 'spanish',       label: 'Spanish'        },
  { value: 'korean',        label: 'Korean'         },
  { value: 'vietnamese',    label: 'Vietnamese'     },
  { value: 'caribbean',     label: 'Caribbean'      },
  { value: 'african',       label: 'African'        },
  { value: 'british',       label: 'British'        },
  { value: 'german',        label: 'German'         },
  { value: 'brazilian',     label: 'Brazilian'      },
  { value: 'fusion',        label: 'Fusion'         },
];

const DIFFICULTY_LEVELS = [
  { value: 'easy',   label: 'Easy'   },
  { value: 'medium', label: 'Medium' },
  { value: 'hard',   label: 'Hard'   },
];

const DIETARY_TAGS = [
  { value: 'vegan',       label: 'Vegan'        },
  { value: 'vegetarian',  label: 'Vegetarian'   },
  { value: 'pescatarian', label: 'Pescatarian'  },
  { value: 'keto',        label: 'Keto'         },
  { value: 'paleo',       label: 'Paleo'        },
  { value: 'glutenFree',  label: 'Gluten-Free'  },
  { value: 'dairyFree',   label: 'Dairy-Free'   },
  { value: 'nutFree',     label: 'Nut-Free'     },
  { value: 'lowCarb',     label: 'Low-Carb'     },
  { value: 'halal',       label: 'Halal'        },
  { value: 'kosher',      label: 'Kosher'       },
  { value: 'highProtein', label: 'High-Protein' },
];

// ---------------------------------------------------------------------------
// DietaryProfileFilter
// Shows the user's care profiles as toggle chips. Selecting one or more
// narrows recipe results to match those profiles' dietary tags and allergens.
// Only rendered for authenticated users who have at least one care profile.
// ---------------------------------------------------------------------------
const PROFILE_COLORS = ['#2D5F4D', '#8B6F47', '#06B6D4', '#7C3AED', '#D97706', '#DC2626'];

const DietaryProfileFilter = ({ careProfiles, selectedProfileIds, onToggle, onClear }) => {
  if (!careProfiles || careProfiles.length === 0) return null;

  return (
    <div
      className="p-md rounded-large bg-surface mb-lg"
      style={{ border: '1px solid rgba(var(--color-text-tertiary),0.1)' }}
    >
      <div className="flex items-center justify-between mb-sm">
        <p className="text-caption font-semibold text-text-secondary uppercase tracking-wide">
          Filter for Care Profile
        </p>
        {selectedProfileIds.length > 0 && (
          <button
            onClick={onClear}
            className="text-caption text-text-tertiary hover:text-primary transition-colors"
          >
            Clear
          </button>
        )}
      </div>
      <div className="flex flex-wrap gap-sm">
        {careProfiles.map(profile => {
          const selected = selectedProfileIds.includes(profile.id);
          const bg = profile.avatarColor || PROFILE_COLORS[(profile.name?.charCodeAt(0) || 0) % PROFILE_COLORS.length];
          return (
            <button
              key={profile.id}
              onClick={() => onToggle(profile.id)}
              className={`flex items-center gap-sm px-md py-sm rounded-full text-caption font-semibold transition-all ${
                selected
                  ? 'text-white shadow-sm'
                  : 'bg-background text-text-secondary hover:text-primary'
              }`}
              style={{
                backgroundColor: selected ? bg : undefined,
                border: selected ? `2px solid ${bg}` : '1px solid rgba(var(--color-text-tertiary),0.2)',
              }}
            >
              <div
                className="w-5 h-5 rounded-full flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0"
                style={{ backgroundColor: selected ? 'rgba(255,255,255,0.3)' : bg }}
              >
                {(profile.name || '?').charAt(0).toUpperCase()}
              </div>
              {profile.name}
            </button>
          );
        })}
      </div>
      {selectedProfileIds.length > 0 && (
        <p className="text-xs text-text-tertiary mt-sm">
          Showing recipes matched to {selectedProfileIds.length === 1 ? 'this profile' : 'these profiles'}
        </p>
      )}
    </div>
  );
};

const RecipesView = () => {
  const [activeTab,       setActiveTab]       = useState('browse');
  const navigate = useNavigate();
  const { recipeId: urlRecipeId } = useParams();
  const [selectedRecipe,  setSelectedRecipe]  = useState(null);
  const [editingRecipe,   setEditingRecipe]   = useState(null);
  const [showSubmission,  setShowSubmission]  = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUserId,   setCurrentUserId]   = useState(null);
  const [selectedCookbook, setSelectedCookbook] = useState(null);

  const [search,           setSearch]           = useState('');
  const [showFilters,      setShowFilters]      = useState(false);
  const [searchParamsRecipes] = useSearchParams();
  const [filterMealType,   setFilterMealType]   = useState(searchParamsRecipes.get('mealType') || '');
  const [filterCuisine,    setFilterCuisine]    = useState(searchParamsRecipes.get('cuisine') || '');
  const [filterDifficulty, setFilterDifficulty] = useState('');
  const [filterDietaryTag, setFilterDietaryTag] = useState('');
  const [filterMaxTime,    setFilterMaxTime]    = useState(null);
  const [viewMode,         setViewMode]         = useState('grid');

  const filters = {
    search,
    mealType:   filterMealType,
    cuisine:    filterCuisine,
    difficulty: filterDifficulty,
    diet:       filterDietaryTag,
    maxTime:    filterMaxTime,
  };

  useEffect(() => {
    const unsub = auth.onAuthStateChanged(user => {
      setIsAuthenticated(!!user);
      setCurrentUserId(user?.uid || null);
      if (!user && ['myRecipes', 'cookbooks', 'saved'].includes(activeTab)) {
        setActiveTab('browse');
      }
    });
    return () => unsub();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Load recipe from URL if /recipes/:recipeId
  const { recipe: urlRecipe } = useRecipe(urlRecipeId || null);

  // Sync URL recipe into selectedRecipe state
  useEffect(() => {
    if (urlRecipe && (!selectedRecipe || selectedRecipe.firestoreId !== urlRecipeId)) {
      setSelectedRecipe(urlRecipe);
    }
  }, [urlRecipe]); // eslint-disable-line react-hooks/exhaustive-deps

  const { recipes, loading }                = useRecipes(filters);
  const { featured, loading: featLoading }  = useFeaturedRecipes();
  const { userRecipes, loading: myLoading } = useUserRecipes(currentUserId);
  const { favoriteIds, toggleFavorite }     = useRecipeFavorites(currentUserId);

  const {
    careProfiles,
    selectedProfileIds,
    isFilterActive: isDietaryFilterActive,
    toggleProfile,
    clearAllProfiles,
    filterRecipes: applyDietaryFilter,
  } = useDietaryFilter();

  const isFavorite = (id) => favoriteIds.has(id);

  const clearFilters = () => {
    setSearch(''); setFilterMealType(''); setFilterCuisine('');
    setFilterDifficulty(''); setFilterDietaryTag(''); setFilterMaxTime(null);
    clearAllProfiles();
  };

  const selectRecipe = (recipe) => {
    const id = recipe.firestoreId || recipe.id;
    setSelectedRecipe(recipe);
    navigate(`/recipes/${id}`);
  };

  const clearRecipe = () => {
    setSelectedRecipe(null);
    navigate('/recipes');
  };

  const savedRecipes = recipes.filter(r => isFavorite(r.firestoreId || r.id));

  // Apply care-profile dietary filter on top of existing search/filter results
  const visibleRecipes     = applyDietaryFilter(recipes);
  const visibleSavedRecipes = applyDietaryFilter(savedRecipes);

  const hasActiveFilters = !!(filterMealType || filterCuisine || filterDifficulty || filterDietaryTag || filterMaxTime || isDietaryFilterActive);

  if (showSubmission || editingRecipe) {
    return (
      <RecipeSubmissionView
        existingRecipe={editingRecipe || null}
        onBack={() => { setShowSubmission(false); setEditingRecipe(null); }}
      />
    );
  }

  if (selectedRecipe) {
    return (
      <RecipeDetailView
        recipe={selectedRecipe}
        onBack={() => clearRecipe()}
        onEdit={(recipe) => { setSelectedRecipe(null); setEditingRecipe(recipe); }}
        isFavorite={isFavorite(selectedRecipe.firestoreId || selectedRecipe.id)}
        onToggleFavorite={toggleFavorite}
        isAuthenticated={isAuthenticated}
      />
    );
  }

  const tabs = [
    { id: 'browse',    label: 'Browse',     icon: BookOpen, auth: false },
    { id: 'myRecipes', label: 'My Recipes', icon: ChefHat,  auth: true  },
    { id: 'cookbooks', label: 'Cookbooks',  icon: Globe,    auth: false },
    { id: 'myBooks',   label: 'My Books',   icon: Book,     auth: true  },
    { id: 'saved',     label: 'Saved',      icon: Heart,    auth: true  },
  ];

  return (
    <div className="max-w-7xl mx-auto">

      <div className="flex gap-xs mb-xl border-b border-text-tertiary/20 overflow-x-auto">
        {tabs.map(tab => {
          const Icon = tab.icon;
          const locked = tab.auth && !isAuthenticated;
          return (
            <button
              key={tab.id}
              onClick={() => { if (locked) { window.location.href = '/login'; return; } setActiveTab(tab.id); }}
              className={`flex items-center gap-xs px-lg py-md text-callout font-semibold whitespace-nowrap flex-shrink-0 transition-all relative ${
                activeTab === tab.id ? 'text-primary border-b-2 border-primary' : 'text-text-secondary hover:text-primary'
              } ${locked ? 'opacity-50' : ''}`}
            >
              <Icon size={16} />{tab.label}
              {locked && <LogIn size={11} className="text-text-tertiary" />}
            </button>
          );
        })}
      </div>

      {/* BROWSE */}
      {activeTab === 'browse' && (
        <div className="space-y-xxxl">

          {/* Header */}
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-display font-rounded text-primary">Recipes</h1>
              <p className="text-body text-text-secondary mt-xs">Discover healthy recipes from our community</p>
            </div>
            {isAuthenticated && (
              <Button variant="primary" onClick={() => setShowSubmission(true)}>
                <Plus size={16} className="mr-xs" />Create Recipe
              </Button>
            )}
          </div>

          {/* Guest notice */}
          {!isAuthenticated && (
            <Card>
              <div className="flex items-center gap-md">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <BookOpen size={20} className="text-primary" />
                </div>
                <div className="flex-1">
                  <p className="text-callout font-semibold text-text-primary">Browsing as Guest</p>
                  <p className="text-caption text-text-secondary">Sign in to save recipes, create cookbooks, and share your own</p>
                </div>
                <Button variant="outline" size="small" onClick={() => window.location.href = '/login'}>Sign In</Button>
              </div>
            </Card>
          )}

          {/* Browse by Cuisine */}
          <section>
            <SectionHeader icon={<Utensils size={20} className="text-primary" />} title="Browse by Cuisine" />
            <div className="flex gap-sm overflow-x-auto pb-sm -mx-4 px-4" style={{ scrollbarWidth: 'none' }}>
              {CUISINE_TYPES.map(c => (
                <button
                  key={c.value}
                  onClick={() => setFilterCuisine(prev => prev === c.value ? '' : c.value)}
                  className={`px-lg py-sm rounded-full text-caption font-semibold whitespace-nowrap flex-shrink-0 transition-all ${
                    filterCuisine === c.value ? 'bg-primary text-white shadow-sm' : 'bg-surface text-text-secondary hover:text-primary'
                  }`}
                  style={{ border: '1px solid rgba(var(--color-text-tertiary),0.15)' }}
                >
                  {c.label}
                </button>
              ))}
            </div>
          </section>

          {/* Browse by Meal Type */}
          <section>
            <SectionHeader icon={<ChefHat size={20} className="text-primary" />} title="Browse by Meal Type" />
            <div className="grid grid-cols-3 md:grid-cols-6 gap-md">
              {MEAL_TYPES.map(m => {
                const Icon = MEAL_TYPE_ICONS[m.value];
                return (
                  <button
                    key={m.value}
                    onClick={() => setFilterMealType(prev => prev === m.value ? '' : m.value)}
                    className={`p-md rounded-large flex flex-col items-center gap-sm transition-all ${
                      filterMealType === m.value ? 'bg-primary text-white shadow-md' : 'bg-surface text-text-primary hover:text-primary'
                    }`}
                    style={{ border: '1px solid rgba(var(--color-text-tertiary),0.1)' }}
                  >
                    {Icon && <Icon size={20} />}
                    <span className="text-caption font-semibold">{m.label}</span>
                  </button>
                );
              })}
            </div>
          </section>

          {/* Trending Recipes */}
          {!featLoading && featured.length > 0 && (
            <section>
              <SectionHeader icon={<TrendingUp size={20} className="text-primary" />} title="Trending Recipes" subtitle="Highest rated by the community" />
              <div className="flex gap-lg overflow-x-auto pb-md -mx-4 px-4" style={{ scrollbarWidth: 'none' }}>
                {featured.map(recipe => (
                  <FeaturedCard
                    key={recipe.firestoreId || recipe.id}
                    recipe={recipe}
                    onClick={() => selectRecipe(recipe)}
                    isFavorite={isFavorite(recipe.firestoreId || recipe.id)}
                    onToggleFavorite={toggleFavorite}
                    isAuthenticated={isAuthenticated}
                  />
                ))}
              </div>
            </section>
          )}

          {/* All Recipes */}
          <section>
            <div className="flex items-center justify-between mb-lg">
              <h2 className="text-title-2 font-bold text-text-primary">
                {hasActiveFilters ? 'Filtered Recipes' : 'All Recipes'}
                {!loading && <span className="text-caption font-normal text-text-tertiary ml-sm">({visibleRecipes.length})</span>}
              </h2>
              <div className="flex items-center gap-sm">
                <div className="flex rounded-medium overflow-hidden" style={{ border: '1px solid rgba(var(--color-text-tertiary),0.15)' }}>
                  <button onClick={() => setViewMode('grid')} className={`px-sm py-xs transition-all ${viewMode === 'grid' ? 'bg-primary text-white' : 'text-text-secondary hover:text-primary'}`}><Grid size={16} /></button>
                  <button onClick={() => setViewMode('list')} className={`px-sm py-xs transition-all ${viewMode === 'list' ? 'bg-primary text-white' : 'text-text-secondary hover:text-primary'}`}><List size={16} /></button>
                </div>
                <button
                  onClick={() => setShowFilters(f => !f)}
                  className={`flex items-center gap-xs px-md py-sm rounded-medium text-caption font-semibold transition-all ${
                    hasActiveFilters ? 'bg-primary text-white' : 'bg-surface text-text-secondary hover:text-primary'
                  }`}
                  style={!hasActiveFilters ? { border: '1px solid rgba(var(--color-text-tertiary),0.15)' } : {}}
                >
                  <SlidersHorizontal size={14} />
                  Filters
                  {hasActiveFilters && (
                    <span className="w-4 h-4 bg-white text-primary rounded-full text-xs flex items-center justify-center font-bold leading-none">
                      {[filterMealType, filterCuisine, filterDifficulty, filterDietaryTag, filterMaxTime].filter(Boolean).length}
                    </span>
                  )}
                </button>
              </div>
            </div>

            <div className="relative mb-lg">
              {isAuthenticated && careProfiles.length > 0 && (
                <DietaryProfileFilter
                  careProfiles={careProfiles}
                  selectedProfileIds={selectedProfileIds}
                  onToggle={toggleProfile}
                  onClear={clearAllProfiles}
                />
              )}
              <Search size={18} className="absolute left-md top-1/2 -translate-y-1/2 text-text-tertiary pointer-events-none" />
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search recipes, ingredients..."
                className="w-full pl-xl pr-lg py-md rounded-medium text-body text-text-primary bg-surface placeholder-text-tertiary focus:outline-none focus:ring-2 focus:ring-primary/30"
                style={{ border: '1px solid rgba(var(--color-text-tertiary),0.2)' }}
              />
              {search && <button onClick={() => setSearch('')} className="absolute right-md top-1/2 -translate-y-1/2 text-text-tertiary hover:text-text-primary"><X size={16} /></button>}
            </div>

            {hasActiveFilters && (
              <div className="flex flex-wrap gap-sm mb-lg">
                {filterMealType   && <FilterPill label={MEAL_TYPES.find(m => m.value === filterMealType)?.label || filterMealType}           onRemove={() => setFilterMealType('')} />}
                {filterCuisine    && <FilterPill label={CUISINE_TYPES.find(c => c.value === filterCuisine)?.label || filterCuisine}           onRemove={() => setFilterCuisine('')} />}
                {filterDifficulty && <FilterPill label={filterDifficulty}                                                                      onRemove={() => setFilterDifficulty('')} />}
                {filterDietaryTag && <FilterPill label={DIETARY_TAGS.find(d => d.value === filterDietaryTag)?.label || filterDietaryTag}      onRemove={() => setFilterDietaryTag('')} />}
                {filterMaxTime    && <FilterPill label={`Max ${filterMaxTime} min`}                                                            onRemove={() => setFilterMaxTime(null)} />}
                <button onClick={clearFilters} className="text-caption font-semibold text-error hover:opacity-70 transition-all">Clear all</button>
              </div>
            )}

            {showFilters && (
              <Card className="mb-lg space-y-lg">
                <div className="flex items-center justify-between">
                  <p className="text-callout font-semibold text-text-primary">Filters</p>
                  <button onClick={() => setShowFilters(false)}><X size={18} className="text-text-secondary" /></button>
                </div>
                <FilterSection label="Difficulty">
                  {DIFFICULTY_LEVELS.map(d => (
                    <TogglePill key={d.value} label={d.label} active={filterDifficulty === d.value} onClick={() => setFilterDifficulty(f => f === d.value ? '' : d.value)} />
                  ))}
                </FilterSection>
                <FilterSection label="Dietary">
                  {DIETARY_TAGS.map(t => (
                    <TogglePill key={t.value} label={t.label} active={filterDietaryTag === t.value} onClick={() => setFilterDietaryTag(f => f === t.value ? '' : t.value)} />
                  ))}
                </FilterSection>
                <div>
                  <p className="text-caption font-semibold text-text-secondary uppercase tracking-wide mb-sm">
                    Max Time: {filterMaxTime ? `${filterMaxTime} min` : 'Any'}
                  </p>
                  <input type="range" min="15" max="180" step="15" value={filterMaxTime || 180}
                    onChange={e => { const v = parseInt(e.target.value); setFilterMaxTime(v < 180 ? v : null); }}
                    className="w-full accent-primary"
                  />
                  <div className="flex justify-between text-caption text-text-tertiary mt-xs"><span>15 min</span><span>3 hrs</span></div>
                </div>
                <Button variant="primary" className="w-full" onClick={() => setShowFilters(false)}>Apply Filters</Button>
              </Card>
            )}

            {loading ? (
              <div className="text-center py-xxxl">
                <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-primary mx-auto mb-md" />
                <p className="text-callout text-text-secondary">Loading recipes...</p>
              </div>
            ) : visibleRecipes.length === 0 ? (
              <Card className="text-center py-xxxl">
                <ChefHat size={48} className="text-text-tertiary/30 mx-auto mb-md" />
                <h3 className="text-title-3 text-primary mb-sm">No recipes found</h3>
                <p className="text-body text-text-secondary mb-lg">
                  {hasActiveFilters ? 'Try adjusting your filters.' : 'Be the first to share a recipe.'}
                </p>
                {hasActiveFilters
                  ? <Button variant="outline" onClick={clearFilters}>Clear Filters</Button>
                  : isAuthenticated && <Button variant="primary" onClick={() => setShowSubmission(true)}>Create Recipe</Button>
                }
              </Card>
            ) : viewMode === 'grid' ? (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-lg">
                {visibleRecipes.map(recipe => (
                  <RecipeCard
                    key={recipe.firestoreId || recipe.id}
                    recipe={recipe}
                    onClick={() => selectRecipe(recipe)}
                    isFavorite={isFavorite(recipe.firestoreId || recipe.id)}
                    onToggleFavorite={toggleFavorite}
                    isAuthenticated={isAuthenticated}
                  />
                ))}
              </div>
            ) : (
              <div className="space-y-md">
                {visibleRecipes.map(recipe => (
                  <RecipeListRow
                    key={recipe.firestoreId || recipe.id}
                    recipe={recipe}
                    onClick={() => selectRecipe(recipe)}
                    isFavorite={isFavorite(recipe.firestoreId || recipe.id)}
                    onToggleFavorite={toggleFavorite}
                    isAuthenticated={isAuthenticated}
                  />
                ))}
              </div>
            )}
          </section>
        </div>
      )}

      {/* MY RECIPES */}
      {activeTab === 'myRecipes' && isAuthenticated && (
        <div>
          <div className="flex items-center justify-between mb-xl">
            <div>
              <h2 className="text-display font-rounded text-primary">My Recipes</h2>
              <p className="text-body text-text-secondary mt-xs">Recipes you have created</p>
            </div>
            <Button variant="primary" onClick={() => setShowSubmission(true)}>
              <Plus size={16} className="mr-xs" />Create Recipe
            </Button>
          </div>
          {myLoading ? (
            <div className="text-center py-xxxl">
              <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-primary mx-auto mb-md" />
              <p className="text-callout text-text-secondary">Loading...</p>
            </div>
          ) : userRecipes.length === 0 ? (
            <Card className="text-center py-xxxl">
              <ChefHat size={48} className="text-text-tertiary/30 mx-auto mb-md" />
              <h3 className="text-title-3 text-primary mb-sm">No recipes yet</h3>
              <p className="text-body text-text-secondary mb-lg">Share your first recipe with the Nutrius community.</p>
              <Button variant="primary" onClick={() => setShowSubmission(true)}>Create Recipe</Button>
            </Card>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-lg">
              {userRecipes.map(recipe => (
                <RecipeCard
                  key={recipe.firestoreId || recipe.id}
                  recipe={recipe}
                  onClick={() => selectRecipe(recipe)}
                  isFavorite={isFavorite(recipe.firestoreId || recipe.id)}
                  onToggleFavorite={toggleFavorite}
                  isAuthenticated={isAuthenticated}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* COOKBOOKS — public discovery, available to guests */}
      {activeTab === 'cookbooks' && (
        selectedCookbook ? (
          <CookbookDetail
            cookbookId={selectedCookbook.id}
            user={isAuthenticated ? { uid: currentUserId } : null}
            onBack={() => setSelectedCookbook(null)}
            onSelectRecipe={(recipe) => {
              setSelectedCookbook(null);
              selectRecipe(recipe);
            }}
          />
        ) : (
          <CookbookBrowse
            user={isAuthenticated ? { uid: currentUserId } : null}
            onSelectCookbook={setSelectedCookbook}
            onCreateCookbook={() => {
              if (!isAuthenticated) { window.location.href = '/login'; return; }
              setActiveTab('myBooks');
            }}
          />
        )
      )}

      {/* MY BOOKS — authenticated user's private cookbook manager */}
      {activeTab === 'myBooks' && isAuthenticated && (
        <CookbookHub
          onNavigateToRecipes={() => setActiveTab('browse')}
          onViewCollection={() => {}}
          onCreateRecipe={() => setShowSubmission(true)}
          onNavigateToRecipe={(recipe) => selectRecipe(recipe)}
        />
      )}

      {/* SAVED */}
      {activeTab === 'saved' && isAuthenticated && (
        <div>
          <h2 className="text-display font-rounded text-primary mb-xl">Saved Recipes</h2>
          {careProfiles.length > 0 && (
            <DietaryProfileFilter
              careProfiles={careProfiles}
              selectedProfileIds={selectedProfileIds}
              onToggle={toggleProfile}
              onClear={clearAllProfiles}
            />
          )}
          {visibleSavedRecipes.length === 0 ? (
            <Card className="text-center py-xxxl">
              <Heart size={48} className="text-text-tertiary/30 mx-auto mb-md" />
              <h3 className="text-title-3 text-primary mb-sm">
                {savedRecipes.length === 0 ? 'No saved recipes' : 'No matching saved recipes'}
              </h3>
              <p className="text-body text-text-secondary mb-lg">
                {savedRecipes.length === 0
                  ? 'Tap the heart on any recipe to save it here.'
                  : 'Try adjusting your care profile filter.'}
              </p>
              {savedRecipes.length === 0
                ? <Button variant="outline" onClick={() => setActiveTab('browse')}>Browse Recipes</Button>
                : <Button variant="outline" onClick={clearAllProfiles}>Clear Filter</Button>
              }
            </Card>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-lg">
              {visibleSavedRecipes.map(recipe => (
                <RecipeCard
                  key={recipe.firestoreId || recipe.id}
                  recipe={recipe}
                  onClick={() => selectRecipe(recipe)}
                  isFavorite={true}
                  onToggleFavorite={toggleFavorite}
                  isAuthenticated={isAuthenticated}
                />
              ))}
            </div>
          )}
        </div>
      )}

    </div>
  );
};

const SectionHeader = ({ icon, title, subtitle }) => (
  <div className="flex items-center gap-sm mb-lg">
    {icon}
    <h2 className="text-title-2 font-bold text-text-primary">{title}</h2>
    {subtitle && <span className="text-caption text-text-tertiary">{subtitle}</span>}
  </div>
);

const FeaturedCard = ({ recipe, onClick, isFavorite, onToggleFavorite, isAuthenticated }) => {
  const imageURL  = getRecipeImageURL(recipe);
  const totalTime = recipe.totalTimeMinutes || 0;
  return (
    <div onClick={onClick} className="relative flex-shrink-0 w-56 h-72 rounded-large overflow-hidden cursor-pointer group">
      {imageURL
        ? <img src={imageURL} alt={recipe.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
        : <div className="w-full h-full bg-gradient-to-br from-primary/30 to-secondary/50 flex items-center justify-center"><ChefHat size={48} className="text-white/30" /></div>
      }
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
      <button
        onClick={e => { e.stopPropagation(); if (!isAuthenticated) { window.location.href = '/login'; return; } onToggleFavorite && onToggleFavorite(recipe.firestoreId || recipe.id); }}
        className={`absolute top-md right-md w-9 h-9 rounded-full flex items-center justify-center transition-all ${isFavorite ? 'bg-error text-white' : 'bg-black/50 text-white hover:bg-error'}`}
      >
        <Heart size={16} className={isFavorite ? 'fill-current' : ''} />
      </button>
      <div className="absolute bottom-0 left-0 right-0 p-md">
        <div className="flex items-center gap-xs mb-xs flex-wrap">
          {recipe.rating > 0 && (
            <span className="flex items-center gap-xs px-sm py-xs bg-accent/90 text-white rounded-full text-xs font-semibold">
              <Star size={10} className="fill-current" />{Number(recipe.rating).toFixed(1)}
            </span>
          )}
          {totalTime > 0 && <span className="flex items-center gap-xs px-sm py-xs bg-black/50 text-white rounded-full text-xs"><Clock size={10} />{totalTime}m</span>}
          {recipe.difficulty && <span className="px-sm py-xs bg-black/50 text-white rounded-full text-xs capitalize">{recipe.difficulty}</span>}
        </div>
        <h3 className="text-callout font-bold text-white leading-tight mb-xs">{recipe.title}</h3>
        {recipe.dietaryTags?.length > 0 && (
          <div className="flex gap-xs flex-wrap">
            {recipe.dietaryTags.slice(0, 2).map(tag => (
              <span key={tag} className="px-xs py-0.5 bg-white/20 text-white rounded-full text-xs capitalize">{tag}</span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const RecipeListRow = ({ recipe, onClick, isFavorite, onToggleFavorite, isAuthenticated }) => {
  const imageURL  = getRecipeImageURL(recipe);
  const totalTime = recipe.totalTimeMinutes || 0;
  return (
    <div onClick={onClick} className="flex items-center gap-md p-md bg-surface rounded-large cursor-pointer hover:shadow-md transition-all group" style={{ border: '1px solid rgba(var(--color-text-tertiary),0.08)' }}>
      <div className="w-20 h-20 rounded-medium overflow-hidden bg-gradient-to-br from-primary/10 to-secondary/10 flex-shrink-0 flex items-center justify-center">
        {imageURL ? <img src={imageURL} alt={recipe.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform" /> : <ChefHat size={24} className="text-text-tertiary/40" />}
      </div>
      <div className="flex-1 min-w-0">
        {recipe.mealType && <p className="text-xs font-semibold text-text-tertiary uppercase tracking-wide mb-xs">{recipe.mealType}</p>}
        <h3 className="text-callout font-semibold text-text-primary line-clamp-2 mb-xs">{recipe.title}</h3>
        <div className="flex items-center gap-md text-caption text-text-secondary">
          {recipe.rating > 0 && <span className="flex items-center gap-xs"><Star size={11} className="text-accent fill-accent" />{Number(recipe.rating).toFixed(1)}</span>}
          {totalTime > 0 && <span className="flex items-center gap-xs"><Clock size={11} />{totalTime}m</span>}
          {recipe.servings > 0 && <span className="flex items-center gap-xs"><Users size={11} />{recipe.servings}</span>}
        </div>
        {recipe.dietaryTags?.length > 0 && (
          <div className="flex gap-xs mt-xs">
            {recipe.dietaryTags.slice(0, 2).map(tag => (
              <span key={tag} className="px-xs py-0.5 bg-primary/10 text-primary text-xs rounded-small font-medium capitalize">{tag}</span>
            ))}
          </div>
        )}
      </div>
      <button
        onClick={e => { e.stopPropagation(); if (!isAuthenticated) { window.location.href = '/login'; return; } onToggleFavorite && onToggleFavorite(recipe.firestoreId || recipe.id); }}
        className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 transition-all ${isFavorite ? 'bg-error text-white' : 'bg-primary/10 text-text-secondary hover:text-error'}`}
      >
        <Heart size={16} className={isFavorite ? 'fill-current' : ''} />
      </button>
    </div>
  );
};

const FilterPill = ({ label, onRemove }) => (
  <span className="flex items-center gap-xs px-md py-xs bg-primary/10 text-primary rounded-full text-caption font-semibold">
    {label}
    <button onClick={onRemove} className="hover:text-error transition-all"><X size={12} /></button>
  </span>
);

const FilterSection = ({ label, children }) => (
  <div>
    <p className="text-caption font-semibold text-text-secondary uppercase tracking-wide mb-sm">{label}</p>
    <div className="flex flex-wrap gap-sm">{children}</div>
  </div>
);

const TogglePill = ({ label, active, onClick }) => (
  <button
    onClick={onClick}
    className={`px-md py-xs rounded-full text-caption font-medium transition-all ${active ? 'bg-primary text-white' : 'bg-surface text-text-secondary hover:text-primary'}`}
    style={!active ? { border: '1px solid rgba(var(--color-text-tertiary),0.15)' } : {}}
  >
    {label}
  </button>
);

export default RecipesView;